import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import LanguageSwitcher from '../components/LanguageSwitcher';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);

  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signIn(username, password);
      // Navigation will be handled by useEffect when user state updates
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="bg-white rounded-2xl shadow-soft hover:shadow-medium transition-all duration-300 border w-full max-w-md p-6 sm:p-8" style={{borderColor: '#05472A20'}}>
        <div className="text-center mb-8">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg overflow-hidden" style={{backgroundColor: '#05472A'}}>
            <img 
              src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
              alt="Logo"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{color: '#05472A'}}>{t('welcomeBack')}</h1>
          <p className="text-sm sm:text-base" style={{color: '#05472A99'}}>{t('signInToAccess')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold mb-3" style={{color: '#05472A'}}>
              {t('username')}
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 sm:px-5 sm:py-4 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300 text-sm sm:text-base" 
              style={{borderColor: '#05472A20'}}
              placeholder={t('enterUsername')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-3" style={{color: '#05472A'}}>
              {t('password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 sm:px-5 sm:py-4 border rounded-xl focus:outline-none focus:ring-2 bg-white transition-all duration-300 text-sm sm:text-base"
              style={{borderColor: '#05472A20'}}
              placeholder={t('enterPassword')}
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white px-4 py-2.5 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium transform hover:scale-105 active:scale-95 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            style={{backgroundColor: '#05472A'}}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('signingIn')}
              </div>
            ) : (
              t('signIn')
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
